import * as React from 'react';
import { createStackNavigator } from '@react-navigation/stack';
import {
  NavigationContainer,
  DefaultTheme,
  DarkTheme,
} from '@react-navigation/native';
import { ColorSchemeName } from 'react-native-appearance';

import { SafeAreaProvider } from 'react-native-safe-area-context';
import { useAuthState, useAuthDispatch } from '../auth/authContext';
import { RESTORE_TOKEN } from '../auth/authTypes';

import { checkAuth } from '../auth/authService';
import SplashScreen from '../components/styled/SplashScreen';
import AuthNavigator from './AuthNavigator';
import RootNavigator from './RootNavigator';

import { AppStackParamList } from '../types/types';
import LinkingConfiguration from './LinkingConfiguration';

// If you are not familiar with React Navigation, we recommend going through the
// "Fundamentals" guide: https://reactnavigation.org/docs/getting-started

const Stack = createStackNavigator<AppStackParamList>();

type RestoredUser = {
  cognitoId: string | null;
  email: string | null;
  token: string | null;
  userType: string | null;
};

export default function AppNavigator({
  colorScheme,
}: {
  colorScheme: ColorSchemeName;
}) {
  const { isLoading, isSignout, userToken } = useAuthState();
  const dispatch = useAuthDispatch();

  React.useEffect(() => {
    const bootstrapAsync = async () => {
      let cognitoId = null;
      let email = null;
      let token = null;
      let userType = null;

      let restoredUser: RestoredUser = {
        cognitoId: cognitoId,
        email: email,
        token: token,
        userType: userType,
      };
      try {
        const user = await checkAuth();
        const { jwtToken, attributes } = user;
        email = attributes.email;
        token = jwtToken;
        userType = attributes['custom:usertype'];

        restoredUser = {
          email,
          token,
          userType,
          cognitoId: attributes.sub,
        };
      } catch (e) {
        console.error('error', e);
      }
      dispatch({ type: RESTORE_TOKEN, ...restoredUser });
    };

    bootstrapAsync();
  }, [dispatch]);
  const renderAuthNavigator = !isLoading && userToken == null;

  return (
    <SafeAreaProvider>
      <NavigationContainer
        linking={LinkingConfiguration}
        theme={colorScheme === 'dark' ? DarkTheme : DefaultTheme}
      >
        <Stack.Navigator headerMode="none">
          {isLoading && (
            // We haven't finished checking for the token yet
            <Stack.Screen name="Splash" component={SplashScreen} />
          )}
          {renderAuthNavigator ? (
            // No token found, user isn't signed in
            <Stack.Screen
              isSignout={isSignout}
              name="Auth"
              component={AuthNavigator}
            />
          ) : (
            // User is signed in
            <Stack.Screen name="Main" component={RootNavigator} />
          )}
        </Stack.Navigator>
      </NavigationContainer>
    </SafeAreaProvider>
  );
}
